<script setup lang="ts">
import { driver } from 'driver.js';
import { escapeHtml } from '~/utils/escapeHtml';
import { findVisibleTarget, shouldAutoStartTour } from '~/utils/experiment/productTour';

const tour = useFreeTierTour();
const { isUae } = useUaeGate();
const route = useRoute();
const { t, localeProperties } = useI18n();

// driver.js buttons take plain strings, so the arrow glyph can't be a flippable
// span (P6b) — pick the glyph that points "forward"/"back" for the text direction.
const isRtl = computed(() => localeProperties.value.dir === 'rtl');
const forwardArrow = computed(() => (isRtl.value ? '←' : '→'));
const backArrow = computed(() => (isRtl.value ? '→' : '←'));
let d: ReturnType<typeof driver> | null = null;

// Resolve a VISIBLE target, waiting for it to paint after a navigation (a selector can match a
// hidden copy, e.g. the desktop nav link at phone width). Generous timeout because cross-page
// steps (/marketing, /learn) fetch data before their target renders.
// A target that is rendered but hidden at this viewport won't appear, so it gets a short grace only.
const HIDDEN_TARGET_GRACE_MS = 750;
const waitForElement = (selector: string, timeout = 4000): Promise<Element | null> =>
  new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const matches = document.querySelectorAll(selector);
      const el = findVisibleTarget(matches);
      if (el) return resolve(el);
      const waited = Date.now() - start;
      if (waited > timeout || (matches.length > 0 && waited > HIDDEN_TARGET_GRACE_MS)) return resolve(null);
      requestAnimationFrame(tick);
    };
    tick();
  });

const destroy = () => {
  d?.destroy();
  d = null;
};

const showCurrentStep = async () => {
  const step = tour.currentStep.value;
  if (!step || route.path !== step.route) return; // wait for navigation; the watcher re-fires
  destroy(); // the previous popover must not stay clickable while we wait (double Next)
  const el = await waitForElement(step.selector);
  if (!tour.active.value || tour.currentStep.value !== step) return; // skipped or moved on while we waited
  // X, overlay click and Esc all end the tour through skip() (driver.js routes them to onDestroyStarted).
  d = driver({ allowClose: true, onDestroyStarted: () => tour.skip() });
  d.highlight({
    // No visible target: driver.js centres the popover without a highlight and the tour keeps going.
    element: el ?? undefined,
    // driver.js renders title/description/button text as HTML — escape the copy.
    popover: {
      title: escapeHtml(t(step.titleKey)),
      description: escapeHtml(t(isUae.value && step.uaeBodyKey ? step.uaeBodyKey : step.bodyKey)),
      // No Back on the first step.
      showButtons: tour.stepIndex.value === 0 ? ['next', 'close'] : ['next', 'previous', 'close'],
      nextBtnText: escapeHtml(
        tour.isLast.value
          ? t('dashboard.freeTierTour.done')
          : t('dashboard.freeTierTour.nextButton', { arrow: forwardArrow.value }),
      ),
      prevBtnText: escapeHtml(t('dashboard.freeTierTour.backButton', { arrow: backArrow.value })),
      onNextClick: () => tour.next(),
      onPrevClick: () => tour.prev(),
    },
  });
};

// React to step/route/active changes: end, navigate, or highlight.
watch(
  [() => tour.active.value, () => tour.stepIndex.value, () => route.path],
  () => {
    if (!tour.active.value) {
      destroy();
      return;
    }
    const step = tour.currentStep.value;
    if (step && route.path !== step.route) {
      destroy(); // clear the current highlight so the page transition is clean
      navigateTo(step.route); // route change re-fires this watcher → showCurrentStep
      return;
    }
    showCurrentStep();
  },
  { flush: 'post' },
);

// Auto-start on the first dashboard visit for a new free-plan lead or a UAE agent.
//
// Reactive — NOT a one-shot onMounted — because tour.start() bails unless
// isFreePlanUser is true, and that flag is backed by the async /api/billing/me
// query which normally resolves AFTER mount. A bare onMounted call would read
// false and never retry, so the tour never ran. Watching isFreePlanUser starts
// it the moment the flag flips true (and start() itself guards hasSeen/active).
const { isFreePlanUser } = useFreePlan();
watch(
  [isFreePlanUser, isUae, () => route.path],
  ([isFree, uae, path]) => {
    const input = { isFreePlanUser: isFree, isUae: uae, path, seen: tour.hasSeen(), active: tour.active.value };
    if (shouldAutoStartTour(input)) tour.start();
  },
  { immediate: true },
);

onBeforeUnmount(destroy);
</script>

<template>
  <!-- driver.js renders its own overlay into <body>; nothing to render here. -->
  <div
    class="hidden"
    aria-hidden="true"
  />
</template>
